import { DOCUMENT } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { DestroyRef, Injectable, NgZone, Optional, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { fromEvent } from 'rxjs';
import { KEYBOARD_SHORTCUT_COMMANDS, OPEN_SITE_SELECTION_SHORTCUT_KEYS } from '@/cpm/shared/constants/keyboard-shortcut.constants';
import { KeyboardShortcutEvent } from '../models/keyboard-shortcut.model';

@Injectable({
	providedIn: 'root'
})
export class KeyboardShortcutService {
	private readonly documentRef = inject(DOCUMENT);
	private readonly serviceDestroyRef = inject(DestroyRef);
	private readonly overlayContainer = inject(OverlayContainer);

	private sequence = 0;
	private readonly shortcutEventSignal = signal<KeyboardShortcutEvent | null>(null);

	readonly shortcutEvent = this.shortcutEventSignal.asReadonly();

	constructor(
		private ngZone: NgZone,
		@Optional() private dialog?: MatDialog
	) {
		// Listen for direct key presses inside the iframe document.
		fromEvent<KeyboardEvent>(this.documentRef, 'keydown')
			.pipe(takeUntilDestroyed(this.serviceDestroyRef))
			.subscribe((event) => this.handleShortcutEventKeydown(event));

		// Listen for shortcut relay messages posted from the parent shell.
		fromEvent<MessageEvent>(this.documentRef.defaultView ?? window, 'message')
			.pipe(takeUntilDestroyed(this.serviceDestroyRef))
			.subscribe((event) => this.handleShortcutMessage(event));
	}

	/**
	 * Returns true when a visible overlay/dialog is currently blocking normal page interactions.
	 * This helps dropdown callers avoid opening behind modal UI.
	 */
	hasBlockingOverlay(hostElement?: HTMLElement | null): boolean {
		const isInsideMatDialog = !!hostElement?.closest(
			'mat-dialog-container, .mat-dialog-container, mat-mdc-dialog-container, .mat-mdc-dialog-container'
		);

		if ((this.dialog?.openDialogs.length || 0) > 0 && !isInsideMatDialog) {
			return true;
		}

		const container = this.overlayContainer.getContainerElement();
		const panes = Array.from(container.querySelectorAll('.cdk-overlay-pane'));
		const defaultView = this.documentRef.defaultView;

		return panes.some((pane) => {
			if (pane.childElementCount === 0) {
				return false;
			}

			if (isInsideMatDialog && pane.querySelector('.mat-dialog-container, .mat-mdc-dialog-container')) {
				return false;
			}

			if (!defaultView) {
				return true;
			}

			const styles = defaultView.getComputedStyle(pane);
			return styles.display !== 'none' && styles.visibility !== 'hidden';
		});
	}

	/**
	 * Handles local keyboard events and emits the org/site open shortcut for plain lowercase `o` or `b`.
	 * Any modifier key, composing input, or typing target is ignored.
	 */
	private handleShortcutEventKeydown(event: KeyboardEvent): void {
		const hasAnyModifier = event.ctrlKey
			|| event.altKey
			|| event.metaKey
			|| event.shiftKey
			|| event.isComposing;

		if (event.defaultPrevented || hasAnyModifier) {
			return;
		}

		const target = event.target as HTMLElement | null;
		if (this.isTypingTarget(target)) {
			return;
		}

		const key = event.key?.trim().toLowerCase();
		const isOrganizationShortcut = key === OPEN_SITE_SELECTION_SHORTCUT_KEYS.ORGANIZATION && event.code === 'KeyO';
		const isBuildingShortcut = key === OPEN_SITE_SELECTION_SHORTCUT_KEYS.BUILDING && event.code === 'KeyB';

		if (!isOrganizationShortcut && !isBuildingShortcut) {
			return;
		}

		this.ngZone.run(() => {
			this.sequence += 1;
			this.shortcutEventSignal.set({
				key,
				command: KEYBOARD_SHORTCUT_COMMANDS.OPEN_ORG_OR_SITE_SELECTION_DROPDOWN,
				sequence: this.sequence,
			});
		});
	}

	/**
	 * Handles trusted shortcut relays from the parent app when focus is outside the iframe.
	 * Only messages matching the strict CPM shortcut payload shape are accepted.
	 */
	private handleShortcutMessage(event: MessageEvent): void {
		let payload: any = event?.data;

		if (typeof payload === 'string') {
			try {
				payload = JSON.parse(payload);
			} catch {
				return;
			}
		}

		const isTrustedOrgRelay = payload?.key === OPEN_SITE_SELECTION_SHORTCUT_KEYS.ORGANIZATION
			&& payload?.source === 'CPM_PLAIN_O';
		const isTrustedBuildingRelay = payload?.key === OPEN_SITE_SELECTION_SHORTCUT_KEYS.BUILDING
			&& payload?.source === 'CPM_PLAIN_B';

		if (
			payload?.type !== '75F_CPM_SHORTCUT'
			|| payload?.command !== KEYBOARD_SHORTCUT_COMMANDS.OPEN_ORG_OR_SITE_SELECTION_DROPDOWN
			|| payload?.plain !== true
			|| (!isTrustedOrgRelay && !isTrustedBuildingRelay)
		) {
			return;
		}

		this.ngZone.run(() => {
			this.sequence += 1;
			this.shortcutEventSignal.set({
				key: payload.key,
				command: KEYBOARD_SHORTCUT_COMMANDS.OPEN_ORG_OR_SITE_SELECTION_DROPDOWN,
				sequence: this.sequence,
			});
		});
	}

	/** Returns true when the keyboard target is a text-entry element. */
	private isTypingTarget(target: HTMLElement | null): boolean {
		if (!target) {
			return false;
		}

		return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
	}
}
